import React, {
  useCallback, useMemo, useRef,
} from 'react';
import {
  Card, CardContent, CardActions, Typography, useTheme,
  Grid, Button, Box, BoxProps, ButtonProps, useMediaQuery, styled,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import EastIcon from '@mui/icons-material/East';
import { mapPlanToPalette } from '../../AppTheme';
import { BillEstimateResponse } from '../../models';
import { changeRatePlan } from '../../reducers/user-input-reducer';
import { getBold } from '../confirmation/ConfirmationDescription';
import { IntlService } from '../../services';

const CardContainer = styled(Card)(({ theme }) => ({
  boxShadow: '0 1px 6px 0 rgba(32, 33, 36, .28)',
  borderRadius: '35px',
  marginTop: '20px',
  display: 'flex',
  flexDirection: 'column',
}));

const CardSubContent = styled(CardContent)(({ theme }) => ({
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const EstimatedSavingPriceContainer = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const CardHeaderContent = styled(Box)<BoxProps>(({ theme }) => ({
  height: '110px',
  padding: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const SelectPlanButton = styled(Button)<ButtonProps>(({ theme }) => ({
  marginBottom: '24px',
  padding: '0 20px',
}));

const CurrentRatePlanContainer = styled(Box)<BoxProps>(({ theme }) => ({
  marginBottom: '44px',
  padding: '0 20px',
  display: 'flex',
  alignItems: 'center',
}));

interface IProps {
  billEstimateResponse: BillEstimateResponse;
  isCurrentPlan: boolean;
}

function getParagraph(chunks: any) {
  return <p>{chunks}</p>;
}

export default function RateCard({
  billEstimateResponse,
  isCurrentPlan,
}: IProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const palette = useMemo(
    () => mapPlanToPalette(billEstimateResponse.planCode),
    [billEstimateResponse.planCode],
  );

  const breakpointForSM = useMediaQuery('(min-width: 600px) and (max-width: 899px)');
  const breakpointForMD = useMediaQuery('(min-width: 901px) and (max-width: 970px)');

  const userLocaleRaw = useRef(IntlService.UserLocaleRaw);

  const choosePlan = useCallback((selectedPlan: string): void => {
    dispatch(changeRatePlan({ selectedPlan }));
    navigate('/confirmation');
  }, [dispatch, navigate]);

  const renderTotalPrice = useCallback((value: number) => Intl.NumberFormat(userLocaleRaw.current, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0,
  }).format(value), [userLocaleRaw]);

  return (
    <CardContainer sx={{ maxWidth: 344 }} data-testid="rate-card-component">
      <CardHeaderContent bgcolor={`${palette}.main`}>
        <Grid container direction="row" justifyContent="space-between" spacing={1}>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            display="flex"
            justifyContent="center"
          >
            <Typography
              sx={{
                typography: {
                  sm: 'body1', xs: 'body2', md: 'body1', lg: 'body1',
                },
              }}
              padding="0 16px"
            >
              <FormattedMessage
                id={`rate-card-name.${billEstimateResponse.planCode}`}
                values={{
                  bold: (chunks: any) => getBold(chunks),
                }}
              />
            </Typography>
          </Grid>
        </Grid>
      </CardHeaderContent>
      <CardSubContent theme={theme} sx={{ marginBottom: 'auto' }}>
        <Box sx={{ minHeight: breakpointForMD ? 300 : 260 }}>
          <Typography variant="body1" component="span">
            <FormattedMessage
              id={`rate-card-desc.${billEstimateResponse.planCode}`}
              values={{
                p: (chunks: any) => getParagraph(chunks),
                strong: (chunks: any) => getBold(chunks),
              }}
            />
          </Typography>
        </Box>
      </CardSubContent>
      <EstimatedSavingPriceContainer theme={theme} sx={{ marginTop: 'auto' }}>
        <Typography
          sx={{
            typography: { sm: breakpointForSM ? 'h3' : 'h2', xs: 'h2', md: breakpointForMD ? 'h3' : 'h2' },
          }}
        >
          {renderTotalPrice(billEstimateResponse.totalPlanCharges)}
        </Typography>
        <Typography variant="body2" mt={2} sx={{ fontSize: 18 }}>
          <FormattedMessage
            id="rate-card-estimated-cost-text"
          />
        </Typography>
        <Box minHeight={40}>
          {billEstimateResponse.estimatedSavings > 0 && (
            <Typography
              sx={{
                typography: {
                  sm: 'body1', xs: 'body1', md: 'body1', lg: 'h6',
                },
                fontWeight: 'bold',
              }}
              color={`${palette}.dark`}
            >
              <FormattedMessage
                id="rate-card-annual-save"
                values={{
                  annualSave: renderTotalPrice(billEstimateResponse.estimatedSavings),
                }}
              />
            </Typography>
          )}
          {billEstimateResponse.estimatedSavings < 0 && !isCurrentPlan && (
            <Typography
              sx={{
                typography: {
                  sm: 'body1', xs: 'body1', md: 'body1', lg: 'h6',
                },
                fontWeight: 'bold',
              }}
              color={theme.palette.error.main}
            >
              {' '}
              <FormattedMessage
                id="rate-card-annual-save-cost-you-more"
                values={{
                  annualSave: renderTotalPrice(Math.abs(billEstimateResponse.estimatedSavings)),
                }}
              />
            </Typography>
          )}
        </Box>
      </EstimatedSavingPriceContainer>
      <CardActions sx={{ justifyContent: 'center' }}>
        { isCurrentPlan ? (
          <CurrentRatePlanContainer>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
              }}
              mb={1}
            >
              <FormattedMessage
                id="rate-card-current-plan"
                values={{
                  bold: (chunks: any) => getBold(chunks),
                }}
              />
            </Typography>
          </CurrentRatePlanContainer>
        )
          : (
            <SelectPlanButton
              variant="contained"
              color={palette}
              endIcon={<EastIcon sx={{ margin: '10px' }} />}
              onClick={() => !isCurrentPlan && choosePlan(billEstimateResponse.planCode)}
            >
              <FormattedMessage
                id="rate-card-select-plan"
              />
            </SelectPlanButton>
          )}
      </CardActions>
    </CardContainer>
  );
}
